/**
 * LLM Models — GET /.cloud/llm/models (#89098)
 *
 * Fetches the browser-safe public model directory for this application,
 * preserving its explicit public fields for display and selection.
 */

import { CloudOpenAIError, parseRetryAfter } from './errors.js';
import type {
    PublicLLMModel,
    PublicModelCapabilities,
    PublicModelContextWindow,
    PublicModelModalities,
    PublicModelPricing,
    PublicModelReasoning,
} from './types.js';

/**
 * Raw public model object returned by GenieBaas.
 *
 * camelCase is the current data-plane contract. PascalCase aliases remain only
 * for a rolling upgrade where an older AgentServer may still return Go default
 * JSON field names. Unknown fields are deliberately not surfaced by the SDK.
 */
export interface RawLLMModel {
    ID?: string;
    id?: string;
    Name?: string;
    name?: string;
    Provider?: string;
    provider?: string;
    Vendor?: string;
    vendor?: string;
    Family?: string;
    family?: string;
    Version?: string;
    version?: string;
    Description?: string;
    description?: string;
    DescriptionZh?: string;
    descriptionZh?: string;
    DescriptionEn?: string;
    descriptionEn?: string;
    IconURL?: string;
    iconUrl?: string;
    ContextWindow?: number | PublicModelContextWindow;
    contextWindow?: number | PublicModelContextWindow;
    MaxInputTokens?: number;
    maxInputTokens?: number;
    MaxOutputTokens?: number;
    maxOutputTokens?: number;
    Modalities?: PublicModelModalities;
    modalities?: PublicModelModalities;
    Capabilities?: PublicModelCapabilities;
    capabilities?: PublicModelCapabilities;
    Enabled?: boolean;
    enabled?: boolean;
    Disabled?: boolean;
    disabled?: boolean;
    IsDefault?: boolean;
    isDefault?: boolean;
    SortOrder?: number;
    sortOrder?: number;
    Pricing?: PublicModelPricing;
    pricing?: PublicModelPricing;

    // ── User-specified public fields (aligned with console productConfig) ──
    Credits?: string;
    credits?: string;
    MaxAllowedSize?: number;
    maxAllowedSize?: number;
    DisabledMultimodal?: boolean;
    disabledMultimodal?: boolean;
    SupportsImages?: boolean;
    supportsImages?: boolean;
    SupportsToolCall?: boolean;
    supportsToolCall?: boolean;
    SupportsReasoning?: boolean;
    supportsReasoning?: boolean;
    OnlyReasoning?: boolean;
    onlyReasoning?: boolean;
    Reasoning?: PublicModelReasoning;
    reasoning?: PublicModelReasoning;
    Temperature?: number;
    temperature?: number;
    // top_k/top_p/repetition_penalty: keep snake_case from productConfig
    top_k?: number;
    Top_K?: number;
    top_p?: number;
    Top_P?: number;
    repetition_penalty?: number;
    Repetition_Penalty?: number;
}

function stringField(...values: unknown[]): string | undefined {
    return values.find((value): value is string => typeof value === 'string' && value.length > 0);
}

function numberField(...values: unknown[]): number | undefined {
    return values.find((value): value is number => typeof value === 'number' && Number.isFinite(value));
}

function booleanField(...values: unknown[]): boolean | undefined {
    return values.find((value): value is boolean => typeof value === 'boolean');
}

/**
 * Normalize the contextWindow controlled union (number | object).
 *
 * The upstream product contract allows both a plain number and an object
 * form (`ModelContextWindow`). This must be handled at runtime — relying on
 * TS type erasure would silently drop the object form like the previous
 * number-only filter did.
 *
 * - number (finite): kept as-is.
 * - object: kept with `supportedLengths` / `defaultLength` validated, and
 *   unknown sub-fields preserved as-is for forward compatibility (matching
 *   the GenieBaas pass-through contract). Special keys such as a JSON-parsed
 *   `__proto__` are preserved as own enumerable properties and never rewrite
 *   the result object's prototype.
 * - anything else (string / boolean / array / null / absent): dropped —
 *   the model entry itself is unaffected.
 */
function contextWindowField(...values: unknown[]): number | PublicModelContextWindow | undefined {
    for (const value of values) {
        if (typeof value === 'number' && Number.isFinite(value)) {
            return value;
        }
        if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
            return normalizeContextWindowObject(value as Record<string, unknown>);
        }
    }
    return undefined;
}

function normalizeContextWindowObject(raw: Record<string, unknown>): PublicModelContextWindow {
    const result: PublicModelContextWindow = {};
    for (const [key, value] of Object.entries(raw)) {
        if (key === 'supportedLengths') {
            if (Array.isArray(value)) {
                result.supportedLengths = value.filter(
                    (item): item is number => typeof item === 'number' && Number.isFinite(item),
                );
            }
            continue;
        }
        if (key === 'defaultLength') {
            if (typeof value === 'number' && Number.isFinite(value)) {
                result.defaultLength = value;
            }
            continue;
        }
        // Unknown sub-fields: preserved as-is for forward compatibility.
        // defineProperty instead of plain assignment: a JSON-origin key named
        // `__proto__` (materialized as an own property by JSON.parse) must stay
        // an own enumerable data property — assignment would instead invoke
        // Object.prototype's `__proto__` setter and rewrite the result's
        // prototype (prototype pollution).
        Object.defineProperty(result, key, {
            value,
            writable: true,
            enumerable: true,
            configurable: true,
        });
    }
    return result;
}

function modalitiesField(value: unknown): PublicModelModalities | undefined {
    if (!value || typeof value !== 'object') return undefined;
    const raw = value as PublicModelModalities;
    const input = Array.isArray(raw.input) ? raw.input.filter((item): item is string => typeof item === 'string') : undefined;
    const output = Array.isArray(raw.output) ? raw.output.filter((item): item is string => typeof item === 'string') : undefined;
    return input?.length || output?.length ? { ...(input?.length ? { input } : {}), ...(output?.length ? { output } : {}) } : undefined;
}

function capabilitiesField(value: unknown): PublicModelCapabilities | undefined {
    if (!value || typeof value !== 'object') return undefined;
    const raw = value as PublicModelCapabilities;
    const entries = Object.entries(raw).filter(([, enabled]) => typeof enabled === 'boolean');
    return entries.length ? Object.fromEntries(entries) as PublicModelCapabilities : undefined;
}

function pricingField(value: unknown): PublicModelPricing | undefined {
    if (!value || typeof value !== 'object') return undefined;
    const raw = value as PublicModelPricing;
    const pricing: PublicModelPricing = {
        ...(stringField(raw.currency) ? { currency: raw.currency } : {}),
        ...(numberField(raw.inputPerMillionTokens) !== undefined ? { inputPerMillionTokens: raw.inputPerMillionTokens } : {}),
        ...(numberField(raw.outputPerMillionTokens) !== undefined ? { outputPerMillionTokens: raw.outputPerMillionTokens } : {}),
        ...(stringField(raw.displayText) ? { displayText: raw.displayText } : {}),
    };
    return Object.keys(pricing).length ? pricing : undefined;
}

/**
 * Normalize a raw reasoning object, preserving sparse sub-fields and explicit false/0.
 * Returns undefined only when the object is absent or has no set fields.
 *
 * SupportedEfforts: when the raw value is an array (including []), the result
 * includes `supportedEfforts` with the filtered string array — even if empty.
 * When the raw value is absent, `supportedEfforts` is omitted.
 *
 * Summary: only 'auto' | 'concise' | 'detailed' are accepted; other strings
 * are dropped to avoid forging an invalid union type.
 */
function reasoningField(value: unknown): PublicModelReasoning | undefined {
    if (!value || typeof value !== 'object') return undefined;
    const raw = value as PublicModelReasoning;
    const result: PublicModelReasoning = {};
    const effort = stringField(raw.effort);
    if (effort !== undefined) result.effort = effort;
    const defaultEffort = stringField(raw.defaultEffort);
    if (defaultEffort !== undefined) result.defaultEffort = defaultEffort;
    // If the raw value is an array, preserve it — even if empty after filtering.
    if (Array.isArray(raw.supportedEfforts)) {
        result.supportedEfforts = raw.supportedEfforts.filter((item): item is string => typeof item === 'string');
    }
    // Summary: only accept console-defined values.
    const rawSummary = stringField(raw.summary);
    if (rawSummary !== undefined && (rawSummary === 'auto' || rawSummary === 'concise' || rawSummary === 'detailed')) {
        result.summary = rawSummary;
    }
    const canDisable = booleanField(raw.canDisableThinking);
    if (canDisable !== undefined) result.canDisableThinking = canDisable;
    return Object.keys(result).length > 0 ? result : undefined;
}

/**
 * Normalize a raw public server model to the SDK's public directory type.
 *
 * Missing optional fields remain absent. The SDK deliberately keeps
 * `enabled: false` entries so a UI can render a disabled state without
 * inventing policy; callers must never treat omitted capabilities as false.
 */
export function normalizeModel(raw: RawLLMModel): PublicLLMModel | null {
    const id = stringField(raw.id, raw.ID);
    if (!id) return null;

    // enabled/disabled compatibility logic:
    // - Prefer server-provided `enabled` when present.
    // - If only `disabled` is present (rolling upgrade), derive enabled = !disabled.
    // - If both absent, default enabled = true.
    const serverEnabled = booleanField(raw.enabled, raw.Enabled);
    const serverDisabled = booleanField(raw.disabled, raw.Disabled);
    let enabled: boolean;
    let disabled: boolean | undefined;
    if (serverEnabled !== undefined) {
        enabled = serverEnabled;
        if (serverDisabled !== undefined) disabled = serverDisabled;
    } else if (serverDisabled !== undefined) {
        disabled = serverDisabled;
        enabled = !serverDisabled;
    } else {
        enabled = true;
    }

    return {
        id,
        name: stringField(raw.name, raw.Name) ?? id,
        ...(stringField(raw.provider, raw.Provider) ? { provider: stringField(raw.provider, raw.Provider) } : {}),
        ...(stringField(raw.vendor, raw.Vendor) ? { vendor: stringField(raw.vendor, raw.Vendor) } : {}),
        ...(stringField(raw.family, raw.Family) ? { family: stringField(raw.family, raw.Family) } : {}),
        ...(stringField(raw.version, raw.Version) ? { version: stringField(raw.version, raw.Version) } : {}),
        ...(stringField(raw.description, raw.Description) ? { description: stringField(raw.description, raw.Description) } : {}),
        ...(stringField(raw.descriptionZh, raw.DescriptionZh) ? { descriptionZh: stringField(raw.descriptionZh, raw.DescriptionZh) } : {}),
        ...(stringField(raw.descriptionEn, raw.DescriptionEn) ? { descriptionEn: stringField(raw.descriptionEn, raw.DescriptionEn) } : {}),
        ...(stringField(raw.iconUrl, raw.IconURL) ? { iconUrl: stringField(raw.iconUrl, raw.IconURL) } : {}),
        ...(contextWindowField(raw.contextWindow, raw.ContextWindow) !== undefined
            ? { contextWindow: contextWindowField(raw.contextWindow, raw.ContextWindow) }
            : {}),
        ...(numberField(raw.maxInputTokens, raw.MaxInputTokens) !== undefined ? { maxInputTokens: numberField(raw.maxInputTokens, raw.MaxInputTokens) } : {}),
        ...(numberField(raw.maxOutputTokens, raw.MaxOutputTokens) !== undefined ? { maxOutputTokens: numberField(raw.maxOutputTokens, raw.MaxOutputTokens) } : {}),
        ...(modalitiesField(raw.modalities ?? raw.Modalities) ? { modalities: modalitiesField(raw.modalities ?? raw.Modalities) } : {}),
        ...(capabilitiesField(raw.capabilities ?? raw.Capabilities) ? { capabilities: capabilitiesField(raw.capabilities ?? raw.Capabilities) } : {}),
        enabled,
        ...(disabled !== undefined ? { disabled } : {}),
        ...(booleanField(raw.isDefault, raw.IsDefault) !== undefined ? { isDefault: booleanField(raw.isDefault, raw.IsDefault) } : {}),
        ...(numberField(raw.sortOrder, raw.SortOrder) !== undefined ? { sortOrder: numberField(raw.sortOrder, raw.SortOrder) } : {}),
        ...(pricingField(raw.pricing ?? raw.Pricing) ? { pricing: pricingField(raw.pricing ?? raw.Pricing) } : {}),
        // User-specified sparse fields: preserve explicit false/0, omit only when absent.
        ...(stringField(raw.credits, raw.Credits) ? { credits: stringField(raw.credits, raw.Credits) } : {}),
        ...(numberField(raw.maxAllowedSize, raw.MaxAllowedSize) !== undefined ? { maxAllowedSize: numberField(raw.maxAllowedSize, raw.MaxAllowedSize) } : {}),
        ...(booleanField(raw.disabledMultimodal, raw.DisabledMultimodal) !== undefined ? { disabledMultimodal: booleanField(raw.disabledMultimodal, raw.DisabledMultimodal) } : {}),
        ...(booleanField(raw.supportsImages, raw.SupportsImages) !== undefined ? { supportsImages: booleanField(raw.supportsImages, raw.SupportsImages) } : {}),
        ...(booleanField(raw.supportsToolCall, raw.SupportsToolCall) !== undefined ? { supportsToolCall: booleanField(raw.supportsToolCall, raw.SupportsToolCall) } : {}),
        ...(booleanField(raw.supportsReasoning, raw.SupportsReasoning) !== undefined ? { supportsReasoning: booleanField(raw.supportsReasoning, raw.SupportsReasoning) } : {}),
        ...(booleanField(raw.onlyReasoning, raw.OnlyReasoning) !== undefined ? { onlyReasoning: booleanField(raw.onlyReasoning, raw.OnlyReasoning) } : {}),
        ...(reasoningField(raw.reasoning ?? raw.Reasoning) ? { reasoning: reasoningField(raw.reasoning ?? raw.Reasoning) } : {}),
        ...(numberField(raw.temperature, raw.Temperature) !== undefined ? { temperature: numberField(raw.temperature, raw.Temperature) } : {}),
        ...(numberField(raw.top_k, raw.Top_K) !== undefined ? { top_k: numberField(raw.top_k, raw.Top_K) } : {}),
        ...(numberField(raw.top_p, raw.Top_P) !== undefined ? { top_p: numberField(raw.top_p, raw.Top_P) } : {}),
        ...(numberField(raw.repetition_penalty, raw.Repetition_Penalty) !== undefined ? { repetition_penalty: numberField(raw.repetition_penalty, raw.Repetition_Penalty) } : {}),
    };
}

/**
 * Return a safe display label for a public model directory entry.
 *
 * `name` is currently required by the SDK type, but retaining the fallback
 * keeps UI code resilient to cached objects produced by an older SDK release.
 */
export function getModelDisplayName(model: PublicLLMModel): string {
    return model.name || model.id;
}

/**
 * Return an explicitly declared capability, or `undefined` when the server
 * did not provide that metadata. `undefined` must not be treated as `false`.
 */
export function getModelCapability(
    model: PublicLLMModel,
    capability: keyof PublicModelCapabilities,
): boolean | undefined {
    return model.capabilities?.[capability];
}

/** LLM models namespace. */
export class ModelsAPI {
    constructor(
        private readonly baseUrl: string,
        private readonly fetch: typeof globalThis.fetch,
    ) {}

    /**
     * List the browser-safe public model directory for this application.
     *
     * `id` is the value to pass as `model` to `chat.completions.create()`.
     * Optional metadata is included only when the server explicitly provides
     * it; absence means unknown, not unsupported. The actual call result
     * is determined by the response, not by the directory entry.
     */
    async list(signal?: AbortSignal): Promise<PublicLLMModel[]> {
        let response: Response;
        try {
            response = await this.fetch(`${this.baseUrl}/models`, {
                method: 'GET',
                headers: { Accept: 'application/json' },
                signal,
            });
        } catch (err) {
            if (err instanceof Error && err.name === 'AbortError') {
                throw err;
            }
            throw new CloudOpenAIError(
                {
                    message: `Network error: ${(err as Error)?.message ?? 'unknown'}`,
                    type: 'server_error',
                    param: null,
                    code: 'gateway_network_error',
                },
                { cause: err },
            );
        }

        if (!response.ok) {
            throw await httpError(response);
        }

        const text = await response.text();
        let parsed: unknown;
        try {
            parsed = JSON.parse(text);
        } catch {
            throw new CloudOpenAIError({
                message: 'Failed to parse models response: invalid JSON',
                type: 'server_error',
                param: null,
                code: 'gateway_invalid_response',
            });
        }

        // Current data-plane contract is a bare array. Accepting { data: [] }
        // preserves compatibility with older deployments without changing the
        // public SDK result type.
        const rawModels: RawLLMModel[] = Array.isArray(parsed)
            ? (parsed as RawLLMModel[])
            : Array.isArray((parsed as { data?: unknown[] })?.data)
                ? ((parsed as { data: RawLLMModel[] }).data)
                : [];

        const models: PublicLLMModel[] = [];
        for (const raw of rawModels) {
            const normalized = normalizeModel(raw);
            if (normalized) models.push(normalized);
        }
        return models;
    }
}

/**
 * Build a CloudOpenAIError from a non-2xx HTTP response.
 *
 * Extracts the OpenAI error object from the response body if present,
 * otherwise synthesizes a fallback from the HTTP status.
 */
export async function httpError(response: Response): Promise<CloudOpenAIError> {
    const text = await response.text().catch(() => '');
    let errorObj: import('./errors.js').OpenAIErrorObject;

    try {
        const parsed = JSON.parse(text);
        if (parsed && typeof parsed === 'object' && parsed.error) {
            const e = parsed.error;
            errorObj = {
                message: typeof e.message === 'string' ? e.message : `HTTP ${response.status}`,
                type: typeof e.type === 'string' ? e.type : 'server_error',
                param: e.param === undefined ? null : e.param,
                code: e.code === undefined ? null : e.code,
            };
        } else {
            errorObj = {
                message: typeof parsed === 'string' ? parsed : `HTTP ${response.status}`,
                type: 'server_error',
                param: null,
                code: null,
            };
        }
    } catch {
        errorObj = {
            message: text || `HTTP ${response.status}`,
            type: 'server_error',
            param: null,
            code: null,
        };
    }

    const requestId = response.headers.get('x-request-id') ?? undefined;
    const retryAfterMs = parseRetryAfter(response.headers.get('retry-after'));

    return new CloudOpenAIError(errorObj, {
        status: response.status,
        requestId,
        retryAfterMs,
    });
}
